import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  ListItem,
  ListItemText,
} from '@mui/material';
import { countries, Country, getUserCountry } from '../data/countries';

interface CountryCodeSelectorProps {
  value: string;
  onChange: (phoneCode: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  // Initialize with user's country or find country by phone code
  useEffect(() => {
    if (value) {
      const country = countries.find(c => c.phoneCode === value);
      setSelectedCountry(country || null);
    } else {
      // Auto-detect user's country and set as default
      const userCountryCode = getUserCountry();
      const userCountry = countries.find(c => c.code === userCountryCode);
      if (userCountry) {
        setSelectedCountry(userCountry);
        onChange(userCountry.phoneCode);
      }
    }
  }, [value, onChange]);

  const handleCountryChange = (event: any, newCountry: Country | null) => {
    setSelectedCountry(newCountry);
    if (newCountry) {
      onChange(newCountry.phoneCode);
    }
  };

  return (
    <Autocomplete
      value={selectedCountry}
      onChange={handleCountryChange}
      options={countries}
      getOptionLabel={(option) => `${option.name} (${option.phoneCode})`}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.code}>
          <Box display="flex" alignItems="center" width="100%">
            <Typography variant="h6" sx={{ mr: 1, fontSize: '1.2em' }}>
              {option.flag}
            </Typography>
            <ListItemText
              primary={
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {option.phoneCode}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Country Code"
          error={error}
          helperText={helperText}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedCountry ? (
              <Typography variant="h6" sx={{ mr: 1, fontSize: '1.2em' }}>
                {selectedCountry.flag}
              </Typography>
            ) : null,
          }}
        />
      )}
      filterOptions={(options, { inputValue }) => {
        if (!inputValue) return options;
        
        const searchTerm = inputValue.toLowerCase();
        return options.filter(option =>
          option.name.toLowerCase().includes(searchTerm) ||
          option.phoneCode.includes(searchTerm) ||
          option.code.toLowerCase().includes(searchTerm) ||
          option.name.toLowerCase().startsWith(searchTerm.charAt(0))
        );
      }}
      autoHighlight
      openOnFocus
      fullWidth
      disabled={disabled}
    />
  );
};

export default CountryCodeSelector;
